import React, { forwardRef } from 'react'

import { Avatar } from '@wartek-id/avatar'
import { Icon } from '@wartek-id/icon'
import { Sheet, SheetCloseButton, SheetContent } from '@wartek-id/sheet'
import { Text } from '@wartek-id/text'

import VideoCard from 'app/VideoRepository/components/VideoCard'
import { capitalizeFirstLetter } from 'utils/text'

import type { PlaylistsReviewer, PlaylistVideo } from 'types/video-inspirasi'

interface PlaylistProps {
  containerRef: React.MutableRefObject<HTMLDivElement>
  reviewer: PlaylistsReviewer
  name: string
  longDescription: string
  shortDescription: string
  playlistId: number
  videoCount: number | string
  videos: PlaylistVideo[]
}

const Playlist = forwardRef(
  ({
    name,
    reviewer,
    longDescription,
    shortDescription,
    videos,
    playlistId,
    videoCount,
  }: PlaylistProps): JSX.Element => {
    const [isShowDetail, setIsShowDetail] = React.useState(false)
    const isShowDetailRef = React.useRef(isShowDetail)

    const toggleShowDetail = () => {
      setIsShowDetail((prevState) => {
        isShowDetailRef.current = !prevState
        return !prevState
      })
    }

    return (
      <>
        <div className="pb-4">
          <div
            className="pb-4 px-4 bg-white"
            style={{
              boxShadow:
                '0 2px 1px rgba(0, 0, 0, .05), 0 0 1px rgba(0, 0, 0, .25)',
            }}
          >
            <Text color="subdued" className="mb-2">
              {capitalizeFirstLetter(shortDescription)}
            </Text>
            <div className="flex items-center">
              <Avatar alt="" shape="circle" size="sm" src={reviewer.imageUrl} />
              <div className="ml-2">
                <Text as="span" color="subdued" variant="body-sm">
                  Dikurasi oleh {reviewer.name}
                </Text>
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={toggleShowDetail}
                className="flex justify-center items-center border-none bg-transparent focus:outline-none"
                type="button"
              >
                <span className="mr-1 font-bold text-interactive-default">
                  Baca selengkapnya
                </span>
                <Icon color="informational">keyboard_arrow_right_icon</Icon>
              </button>
            </div>
          </div>
          <div className="py-4">
            <Text variant="heading-sm" className="ml-4 mb-4">
              {videoCount} Video
            </Text>
            {videos.map(({ title, videoId, duration, thumbnailUrl }, index) => (
              <VideoCard
                key={index}
                playlistId={playlistId}
                thumbnailUrl={thumbnailUrl}
                duration={duration}
                title={title}
                videoId={videoId}
              />
            ))}
          </div>
        </div>
        <Sheet isOpen={isShowDetail} onClose={setIsShowDetail} sheetSize="full">
          <SheetContent>
            <div className="px-4 pt-14 pb-4">
              <div className="pb-5">
                <Text variant="heading-lg" className="mb-2">
                  {name}
                </Text>
                <Text
                  className="simple-wysiwyg-content material-content video-inspirasi"
                  dangerouslySetInnerHTML={{
                    __html: longDescription,
                  }}
                />
                <div className="mt-4">
                  <div className="mb-1">
                    <Text variant="heading-sm">Daftar Video:</Text>
                  </div>
                  <Text as="p">
                    <ul className="list-disc pl-7">
                      {videos.map((video, index) => {
                        return (
                          <li
                            className="text-subdued"
                            key={video.videoId + '_' + index}
                          >
                            {video.title}
                          </li>
                        )
                      })}
                    </ul>
                  </Text>
                </div>
              </div>
              {/*Video list*/}
              <div className="py-5 border-t border-subdued">
                <div className="flex items-center mb-4">
                  <div className="mr-3">
                    <Text variant="body-sm" className="mb-1">
                      Dikurasi oleh
                    </Text>
                    <Text variant="heading-sm">{reviewer.name}</Text>
                  </div>
                  <div className="ml-auto">
                    <Avatar
                      alt=""
                      shape="circle"
                      size="xl"
                      src={reviewer.imageUrl}
                    />
                  </div>
                </div>
                <div>
                  <Text>{reviewer.about}</Text>
                </div>
              </div>
              {/*Reviewer bio*/}
            </div>
            <SheetCloseButton onClick={toggleShowDetail} />
          </SheetContent>
        </Sheet>
        {/* )} */}
      </>
    )
  }
)

Playlist.displayName = 'Playlist'

export default Playlist
